import React, { useCallback, useEffect, useState } from "react";
import { NoLocation as NoLocationSelected } from "./NoLocation";
import { TopbarProps } from "./types";
import { updateLocation as SyncApiLocation } from "~/assets/js/Api/storeApi";
import { dispatchLocationChange } from "../../Api/storeApi";
import { fetchStoreOpeningTimes } from "../../Api/deliveryTimesApi";
import { LocationSelected } from "./LocationSelected";
import i18next from "i18next";
import useAreaCookie from "~/assets/js/Helpers/hooks/useAreaCookie";
import AddressPopup from "../AddressPopup";
import { wait } from "../../Helpers/util";
import useCustomEvent from "../../Helpers/hooks/useCustomEvent";
import cartSession from "../../global/cartSession";
import { useGroupCart } from "../../Api/cartApi";
import GroupOrderBanner from "../GroupOrderBanner";
import Popup from "../Popup";
import Button from "../Button";
import GreenclubRound from "./greenclub-round.svg"
import GreenclubText from "./greenclub-text.svg"

export const DELIVERY_TYPES = {
    DELIVERY: "delivery",
    PICKUP: "pickup",
};

const Topbar = ({ disableHeader }) => {
    const cookie = useAreaCookie();
    const [location, setLocation] = useState(null);
    const [isInitialized, setInit] = useState(false);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [storeInformation, setStoreInfo] = useState(null);
    const [forceOverlay, setForceOverlay] = useState(null);
    const { triggerEvent } = useCustomEvent("product-callback-event");
    const cartSessionData = cartSession.get();
    const isGroupOrder = Boolean(cartSessionData?.group_cart);
    const isGroupParticipant = (({ cart, isGroupOrder }) => {
        const ownerUid = cart?.owner_uid;
        return isGroupOrder && !ownerUid;
    })({
        cart: cartSessionData,
        isGroupOrder,
    });

    const groupUid = cartSessionData?.group_uid;
    const { data: groupCartData } = useGroupCart({
        uid: groupUid,
        enabled: isGroupOrder,
    });

    const fetchOpeningTimes = async () => {
        const response = await fetchStoreOpeningTimes();
        if (response.ok) {
            setStoreInfo(response.storeInformation);
        }
    };

    useEffect(() => {
        if (window.cookie.isSet()) {
            const { address, area } = cookie;
            setLocation({ address, area });
        }

        setInit(true);
    }, [cookie]);

    useEffect(() => {
        if (isInitialized) {
            fetchOpeningTimes();
        }
    }, [location]);

    const onLocationSelect = async (
        area_update,
        location_update,
        address_update,
        shouldCloseOverlay = true
    ) => {
        const newLocation = {
            area: area_update,
            location: location_update,
            address: address_update,
        };

        dispatchLocationChange(newLocation);

        setLocation({ area: newLocation.area, address: newLocation.address });

        await SyncApiLocation();

        if (forceOverlay !== null) {
            await wait(500);
            triggerEvent({
                callbackAction: forceOverlay.callbackAction,
            });
            setForceOverlay(null);
        }

        shouldCloseOverlay && setOverlayOpen(false);
    };

    const renderTopBarContent = useCallback(() => {
        const props = {
            initialized: isInitialized,
            setOverlayOpen,
            setForceOverlay,
        };

        if (location === null) {
            return <NoLocationSelected {...{ ...props }} />;
        }

        const { type: deliveryType } = location?.area;

        let icon;
        let address;
        const { iconPath } = reactConfig;

        switch (deliveryType) {
            case DELIVERY_TYPES.PICKUP:
                icon = `${iconPath}/shop-new.svg`;
                const pickupText = i18next.t("topbar.label.pickupAt");
                const pickupArea = location.area.name.replace("Abholung", "");
                address = `${pickupText} ${pickupArea}`;
                return (
                    <LocationSelected
                        {...{
                            ...props,
                            icon,
                            address,
                            storeInformation,
                            fetchStoreData: fetchOpeningTimes,
                        }}
                    />
                );
            case DELIVERY_TYPES.DELIVERY:
                icon = `${iconPath}/bicycle-new.svg`;
                const deliveryText = i18next.t("topbar.label.deliveryTo");
                address = `${deliveryText} ${location.address.address} ${location.address.housenumber}`;
                const isXXL = location.area?.category
                    .toLowerCase()
                    .includes("xxl");

                return (
                    <LocationSelected
                        {...{
                            ...props,
                            icon,
                            address,
                            storeInformation,
                            isXXL,
                            fetchStoreData: fetchOpeningTimes,
                        }}
                    />
                );
            default:
                return null;
        }
    }, [location, isInitialized, storeInformation]);

    const renderGroupOrderBanner = () => {
        if (!groupCartData) {
            return null;
        }

        return (
            <GroupOrderBanner
                isOwner={!isGroupParticipant}
                ownerName={groupCartData?.owner_name || ""}
                slotEnd={groupCartData?.slot_valid_until || ""}
            />
        );
    };

    if (disableHeader) {
        return null;
    }

    return (
        <>
            <Popup
                open={
                    overlayOpen ||
                    reactConfig?.greenClub?.includes(
                        location?.area?.store_id?.toString()
                    )
                }
                handleClose={() => setOverlayOpen(true)}
                customStyle="green-club-redirect">
                <img
                    src={GreenclubRound}
                    alt="Greenclub Logo Round"
                    className="desktop"
                />
                <img
                    src={GreenclubText}
                    alt="Greenclub Logo Text"
                    className="mobile"
                />
                <span>
                    Neueröffnung: aus Pottsalat & MaKE. wurde Green Club! Jetzt
                    Webshop entdecken und direkt bestellen! 💚
                </span>
                <Button
                    href="https://green.club"
                    label="Zu green.club wechseln"
                />
            </Popup>
            <div id="topbar">
                {isGroupOrder ? (
                    renderGroupOrderBanner()
                ) : (
                    <div className="topbar-content">
                        {renderTopBarContent()}
                    </div>
                )}
            </div>
        </>
    );
};

Topbar.propTypes = TopbarProps;
export default Topbar;
